<template>
  <!-- Middle panel -->
  <HeaderBar :modifiers="['dark', 'sticky']" class="hide-for-medium">
    <router-link
      class="header-bar__title typo-nav-item"
      :to="{ name: 'shaftDefinition' }"
      :aria-label="$t('general.back')"
    >
      <UseSvg name="arrow_left" class="icon" />
      <div class="typo-small-title">
        {{ $t(`shafts.element_types.shaft.${$route.params.elementType}`) }}
      </div>
    </router-link>
  </HeaderBar>

  <div class="definition-layout__group">
    <div class="definition-layout__group--content definition-layout__group--content--padding-around definition-layout__group--content--less-view-height">
      <Loader v-if="loading" />

      <!-- element type header -->
      <HeaderBar :modifiers="['no-padding']" v-if="!loading">
        <div>
          <strong class="typo-title">
            {{ $t(`shafts.element_types.shaft.${$route.params.elementType}`) }}
            <span class="typo-span">
              {{
                pluralize(
                  activeElementGroup?.length,
                  $t('shafts.group.group'),
                  $t('shafts.group.groups')
                )
              }}
            </span>
          </strong>
        </div>
        <template #right>
          <a @click="newGroupModal" :class="{ 'button' : true, 'button--disabled' : !shaft?.policy?.edit }">
            <span class="button__label show-for-large">
              {{ addGroupButtonText }}
            </span>
            <UseSvg name="plus" class="icon icon--small button__icon button__icon--left-space" />
          </a>
        </template>
      </HeaderBar>

      <!-- element group list -->

      <div
        class="header-group-wrapper"
        v-for="elementGroup in activeElementGroup"
        :key="elementGroup.id"
      >
        <HeaderBar :modifiers="['dark', 'no-padding-right', 'margin-bottom', elementGroup?.shaft_elements?.length <= 0 ? 'radius' : 'radius-no-bottom']" v-if="!loading">
          <div>
            <strong class="typo-title">{{ elementGroup?.name }} <span class="typo-span">{{ elementGroup?.shaft_elements?.length }}</span></strong>
          </div>
          <template #right>
            <router-link
              :class="{'button button--margin-right': true, 'button--disabled' : !shaft?.policy?.edit}"
              :to="{ name: 'newShaftElementForm', params: { elementGroupId: elementGroup.id } }"
            >
              <UseSvg name="plus" class="icon icon--small button__icon" />
            </router-link>
          </template>
        </HeaderBar>

        <!-- element list -->
        <div
          v-for="(element, index) in elementGroup?.shaft_elements"
          :key="element.id"
          :class="{'header-bar-wrapper' : true, 'header-bar-wrapper--radius' : index === elementGroup?.shaft_elements?.length-1 }"
        >
          <HeaderBar :modifiers="['light', 'radius', 'less-padding']" v-if="!loading">
            <div class="element element--group">
              <strong class="typo-title">{{ element?.name }}</strong>
              <p>
                <span
                  v-if="element?.geometry?.x || element?.geometry?.y"
                  class="typo-span"
                >
                  {{ `x: ${element?.geometry?.x}, y: ${element?.geometry?.y}` }}
                </span>
                <span
                  v-if="element?.geometry?.width || element?.geometry?.height"
                  class="typo-span"
                >
                  {{ `, ${element?.geometry?.width}x${element?.geometry?.height}` }}
                </span>
                <span
                  v-if="element?.geometry?.size"
                  class="typo-span"
                >
                  {{ `, size: ${element?.geometry?.size}` }}
                </span>
                <span
                  v-if="element?.geometry?.type"
                  class="typo-span"
                >
                  {{ `, type: ${element?.geometry?.type}` }}
                </span>
              </p>
            </div>
            <template #right>
              <Dropdown
                class="element element--menu"
                :items="elementMenuItems(element)"
                @click-item="deleteDialog(element.id, $event)"
              >
                <UseSvg name="vertical_ellipsis" class="icon" />
              </Dropdown>
            </template>
          </HeaderBar>
        </div>
      </div>

      <EmptyMessage
        v-if="activeElementGroup?.length <= 0"
        :title="$t(`empty_states.no_element`, { type: $t(`shafts.element_types.shaft.${$route.params.elementType}`) })"
        :message="$t(`empty_states.create_group`, { type: $t(`shafts.element_types.shaft.${$route.params.elementType}`) })"
      >
        <div class="empty-message__image">
          <ExclamationOctogonSVG />
        </div>
      </EmptyMessage>
    </div>

    <!-- Shaft diagram (right panel) -->
    <div class="definition-layout__right">
      <Loader v-if="loadingDiagram || !shaft" />
      <DefinitionDiagram
        v-else
        :modifiers="['border-bottom']"
        :elementType="elementType"
        :shaftDiagramData="shaftDiagramData"
        :shaftDiagramVisible="$route.params.elementType !== 'stations'"
      />
    </div>

    <!-- Shaft popup form for creating groups -->
    <ShaftGroupForm
      v-model:modalIsOpen="modalIsOpen"
      :elementType="elementType"
      @save="createGroup"
    />

    <!-- Shaft popup menu for editing or deleting items -->
    <Dialog
      v-if="dialogIsOpen"
      :title="$t('shafts.group.delete_confirmation_title')"
      :message="$t('shafts.group.delete_confirmation_message')"
      @dismissDialog="toggleDialog"
    >
      <template #actions>
        <button
          aria-label="delete-element"
          @click="deleteElement"
          class="button button--inverse"
        >
          {{ $t('general.delete') }}
        </button>
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import useAjax from '@/functions/use_ajax';
import { useShaft } from '@/functions/use_remote_object';
import I18n from 'i18n-js';

import HeaderBar from '@/components/generic/header_bar.vue';
import DefinitionDiagram from '@/components/shafts/definition/definition_diagram.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import Loader from '@/components/generic/loader.vue';
import ExclamationOctogonSVG from '@/images/exclamation_octogon.svg';
import UseSvg from '@/components/generic/use_svg.vue';
import Dropdown from '@/components/generic/dropdown.vue';
import ShaftGroupForm from '@/components/shafts/definition/group_form.vue';
import Dialog from '@/components/generic/dialog.vue';
import pluralize from '@/functions/pluralize';

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
  elementType: { type: String, required: true },
});

const ajax = useAjax();
const route = useRoute();
const shaft = useShaft(props);

const loading = ref(false);
const loadingDiagram = ref(null);
const crossSectionElements = ref(null);
const activeElementGroup = ref(null);
const shaftElementGroups = ref(null);
const selectedElementId = ref(null);

const modalIsOpen = ref(false);
const menuOpen = ref(false);
const dialogIsOpen = ref(false);

onMounted(() => {
  loadShaftDiagram();
  loadShaftGroup();
});

/**
 * Get shaft cross-section diagram data
 */
const loadShaftDiagram = async () => {
  loadingDiagram.value = true;

  let { ok, data } = await ajax(`/organisations/${props.organisationId}/shafts/${props.shaftId}/dashboard/geometry`);

  if (ok) {
    crossSectionElements.value = data;
  }

  loadingDiagram.value = false;
};

/**
 * Get shaft element groups
 */
const loadShaftGroup = async () => {
  loading.value = true;

  let { ok, data } = await ajax(`/organisations/${props.organisationId}/shafts/${props.shaftId}/shaft_element_groups`);

  if (ok) {
    shaftElementGroups.value = data;
    loadShaftElement();
  }

  loading.value = false;
};

/**
 * Get shaft selected element
 */
const loadShaftElement = () => {
  if (!shaftElementGroups.value) return;
  activeElementGroup.value = shaftElementGroups.value.filter(result => {
    return (result.element_type).toLowerCase() === (route.params.elementType).toString().toLowerCase();
  }) || [];
};

watch(() => props.elementType, loadShaftDiagram);
watch(() => props.elementType, loadShaftElement);

/**
 * Data for the shaft diagram,
 * passed through the DefinitionDiagram component
 */
const shaftDiagramData = computed(() => {
  return {
    elements: crossSectionElements.value,
    shape: shaft.value?.shape,
    activeItem: activeElementGroup.value,
  };
});

/**
 * Gets the group button text and
 * formats it into sentence case
 */
const addGroupButtonText = computed(() => {
  return I18n.t('general.add', { type: (I18n.t(`shafts.element_types.shaft.${route.params.elementType}`)).toLowerCase() });
});

/**
 * Displays the new group modal
 */
function newGroupModal() {
  modalIsOpen.value = true;
}

/**
 * Submit new group
 */
async function createGroup(group) {
  group.shaft_elements = [];
  shaftElementGroups.value = [...shaftElementGroups.value, group];
  loadShaftElement();
}

function toggleDialog() {
  dialogIsOpen.value = !dialogIsOpen.value;
  menuOpen.value = false;
}

async function deleteElement() {
  let { ok } = await ajax(
    `/organisations/${props.organisationId}/shafts/${props.shaftId}/shaft_elements/${selectedElementId.value}`,
    { method: 'DELETE' }
  );

  if (ok) {
    toggleDialog();
    loadShaftGroup();
  }
}

/**
 * Opens the dialogs and stores the id
 */
function deleteDialog(id) {
  selectedElementId.value = id;
  dialogIsOpen.value = !dialogIsOpen.value;
}

function elementMenuItems(element) {
  const matchElement = !!crossSectionElements.value && crossSectionElements.value[element.element_type]?.find(item => item.id === element.id);
  return [
    {
      label: I18n.t('general.edit_field', { field_name: element.name }),
      to: { name: 'editShaftElementForm', params: { shaftId: props.shaftId, elementId: element.id, elementGroupId: element.shaft_element_group_id } },
      modifiers: [!shaft.value?.policy?.edit ? 'disabled' : null]
    },
    {
      label: I18n.t('general.delete'),
      key: 'delete',
      modifiers: ['destructive', !matchElement?.policy?.destroy ? 'disabled' : null]
    },
  ];
}

</script>
<style scoped lang="scss">
@import '@/stylesheets/global';

.typo-span {
  font-weight: normal;
  color: map-get($foreground-color, dark);
}
.header-group-wrapper {
  margin-bottom: bu(1);
}
.header-bar-wrapper {
  width: 100%;
  background-color: map-get($background-color, dark);
  padding: bu(0.5) bu(1);

  &:nth-child(2) {
    padding-top: bu(1);
  }
  &:last-child {
    padding-bottom: bu(1);
  }

  &--radius {
    border-bottom-left-radius: bu(2);
    border-bottom-right-radius: bu(2);
  }

  @include breakpoint(medium) {
    &:hover {
      .element--menu {
        visibility: visible;
      }
    }
  }
}
.element {
  &--group {
    width: 100%;
  }

  @include breakpoint(medium) {
    &--menu {
      cursor: pointer;
      visibility: hidden;
    }
  }
}
</style>
